import React, { useState, useEffect, useRef } from 'react';
import { getUserQuizzes } from '../../utils/quizService';
import { TOTAL_SESSION_QUESTIONS, EXTRA_QUIZ_COUNT_KEY } from '../../config/quizConfig';
import './ReviewQuestions.css';

const SURVEY_URL = 'https://forms.gle/sz6PAFrjUYZb28Ay8';

const ReviewQuestions = () => {
  const [reviewQuestions, setReviewQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedQuestion, setExpandedQuestion] = useState(null);
  const [reviewedQuestions, setReviewedQuestions] = useState(new Set()); // Track reviewed questions for UI state only
  const [showCompletionPopup, setShowCompletionPopup] = useState(false);
  const [showTreatsCollectedPopup, setShowTreatsCollectedPopup] = useState(false);
  const [completedReviewsCount, setCompletedReviewsCount] = useState(0);
  const [showVideoPopup, setShowVideoPopup] = useState(false);
  const videoRef = useRef(null);

  // Initialize session start time on component mount
  // This tracks when the session started to only show questions from this session
  useEffect(() => {
    // Check if this is a new login session (sessionStorage was cleared on logout)
    const sessionActive = sessionStorage.getItem('reviewSessionActive');
    const sessionStartTime = sessionStorage.getItem('reviewSessionStartTime');
    
    if (!sessionActive || !sessionStartTime) {
      // New session - mark session as active
      const startTime = Date.now();
      sessionStorage.setItem('reviewSessionActive', 'true');
      sessionStorage.setItem('reviewSessionStartTime', startTime.toString());
      console.log('🆕 New session started at', new Date(startTime).toLocaleTimeString());
    }
  }, []);

  // Load other persisted data from localStorage on mount
  useEffect(() => {
    // Load completed reviews count
    const savedCount = localStorage.getItem('completedReviewsCount');
    if (savedCount) {
      try {
        const count = parseInt(savedCount, 10) || 0;
        setCompletedReviewsCount(count);
      } catch (e) {
        console.error('Error loading completed reviews count:', e);
      }
    }
    
    // Check if completion popup has been shown
    const completionShown = localStorage.getItem('reviewCompletionShown');
    if (completionShown) {
      setShowCompletionPopup(false);
    }
  }, []);

  // Fetch questions when viewedQuestions is initialized or changes
  useEffect(() => {
    const fetchReviewQuestions = async () => {
      try {
        setLoading(true);
        
        // Get session start time to only show questions from this session
        const sessionStartTime = sessionStorage.getItem('reviewSessionStartTime');
        const sessionStart = sessionStartTime ? parseInt(sessionStartTime, 10) : null;
        
        const data = await getUserQuizzes();
        
        if (data.quizzes && data.quizzes.length > 0) {
          // Sort quizzes by timestamp (most recent first)
          const sortedQuizzes = [...data.quizzes].sort((a, b) => {
            const dateA = a.created_at ? new Date(a.created_at).getTime() : 0;
            const dateB = b.created_at ? new Date(b.created_at).getTime() : 0;
            return dateB - dateA; // Descending order (newest first)
          });
          
          // Filter to only show questions created AFTER session start (this login)
          let sessionQuizzes = sortedQuizzes;
          if (sessionStart) {
            sessionQuizzes = sortedQuizzes.filter(quiz => {
              const quizTime = quiz.created_at ? new Date(quiz.created_at).getTime() : 0;
              return quizTime >= sessionStart;
            });
            console.log(`🕐 Filtered to ${sessionQuizzes.length} questions from this session (started at ${new Date(sessionStart).toLocaleTimeString()})`);
          }
          
          // Get the most recent questions for review — one full reading session's worth
          // (scheduled quizzes plus any extra quizzes taken, 1 question each).
          // Questions persist during the session - only cleared on logout
          let extraQuizzes = 0;
          try { extraQuizzes = parseInt(sessionStorage.getItem(EXTRA_QUIZ_COUNT_KEY), 10) || 0; } catch (e) { /* none */ }
          const recentQuizzes = sessionQuizzes.slice(0, TOTAL_SESSION_QUESTIONS + extraQuizzes);
          
          console.log(`📊 Fetched ${recentQuizzes.length} questions from this session`);
          setReviewQuestions(recentQuizzes);
        } else {
          setReviewQuestions([]);
        }
        setLoading(false);
      } catch (error) {
        console.error('Error fetching review questions:', error);
        setLoading(false);
        setReviewQuestions([]);
      }
    };

    // Fetch questions on mount and when topicUrl changes
    // Questions persist during session - only cleared on logout
    fetchReviewQuestions();
  }, []); // Only fetch once on mount - questions persist during session

  // Check if all questions have been reviewed
  useEffect(() => {
    if (reviewQuestions.length > 0 && reviewedQuestions.size > 0) {
      const allQuestionIds = reviewQuestions.map((quiz, index) => 
        quiz.id || quiz.quiz_id || `quiz_${index}_${quiz.created_at}`
      );
      const allReviewed = allQuestionIds.every(id => reviewedQuestions.has(id));
      
      // Check if completion popup has been shown for this session
      const completionShown = localStorage.getItem('reviewCompletionShown');
      
      if (allReviewed && !completionShown) {
         // Increment treats collected so the cookie appears in Treats Collected box
        const savedCount = localStorage.getItem('completedReviewsCount');
        const currentCount = parseInt(savedCount, 10) || 0;
        const newCount = currentCount >= 8 ? 1 : currentCount + 1;
        setCompletedReviewsCount(newCount);
        localStorage.setItem('completedReviewsCount', newCount.toString());

        // If 8th treat unlocked, show video popup; otherwise show cookie completion popup
        if (newCount === 8 && currentCount === 7) {
          setShowVideoPopup(true);
        } else {
        setShowCompletionPopup(true);
        }
        localStorage.setItem('reviewCompletionShown', 'true');
      }
    }
  }, [reviewQuestions, reviewedQuestions]);

  const toggleQuestion = (index) => {
    const quiz = reviewQuestions[index];
    const quizId = quiz.id || quiz.quiz_id || `quiz_${index}_${quiz.created_at}`;
    
    // When question is expanded, just mark it as reviewed for UI state
    // Questions persist during the session - only cleared on logout
    if (expandedQuestion !== index) {
      // Mark as reviewed for UI state (visual indication only)
      const newReviewed = new Set(reviewedQuestions);
      newReviewed.add(quizId);
      setReviewedQuestions(newReviewed);
      
      // Set the expanded question to the current one
      setExpandedQuestion(index);
    } else {
      // Just collapse the question
      setExpandedQuestion(null);
    }
  };

  const closeCompletionPopup = () => {
    setShowCompletionPopup(false);
  };


  const handleTreatsCollected = () => {
    // Show the treats collected popup
    setShowTreatsCollectedPopup(true);
  };

  const closeTreatsCollectedPopup = () => {
    setShowTreatsCollectedPopup(false);
  };

  const closeVideoPopup = () => {
    setShowVideoPopup(false);
  };

  const handleVideoEnded = () => {
    // Just close the video popup, don't show completion popup
    setShowVideoPopup(false);
  };

  return (
    <div className="review-questions-container">
      <h3 className="review-questions-title">Review what you learned today!</h3>
      
      {loading ? (
        <div className="review-questions-loading">Loading review questions...</div>
      ) : reviewQuestions.length === 0 ? (
        <div className="review-questions-empty">
          <p>No quiz questions available for review yet.</p>
          <p>Complete some quizzes to see your review questions!</p>
        </div>
      ) : (
        <div className="review-questions-list">
          {reviewQuestions.map((quiz, index) => {
            const quizId = quiz.id || quiz.quiz_id || `quiz_${index}_${quiz.created_at}`;
            const isReviewed = reviewedQuestions.has(quizId);
            
            return (
            <div 
              key={quiz.id || index} 
              className={`review-question-item ${quiz.isCorrect ? 'correct' : 'incorrect'} ${isReviewed ? 'reviewed' : ''}`}
            >
              <div 
                className="review-question-header"
                onClick={() => toggleQuestion(index)}
              >
                <div className="review-question-info">
                  <span className="review-question-number">Q{index + 1}</span>
                  <span className="review-question-title">{quiz.title || 'Quiz Question'}</span>
                  <span className={`review-question-status ${quiz.isCorrect ? 'status-correct' : 'status-incorrect'}`}>
                    {quiz.isCorrect ? '✓ Correct' : '✗ Incorrect'}
                  </span>
                </div>
                <div className="review-question-expand">
                  {expandedQuestion === index ? '▼' : '▶'}
                </div>
              </div>
              {expandedQuestion === index && (
                <div className="review-question-details">
                  {/* Question Text */}
                  {quiz.question_text ? (
                    <div className="review-question-text">
                      <strong>Question:</strong>
                      <p>{quiz.question_text}</p>
                    </div>
                  ) : (
                    <div className="review-question-text" style={{ color: '#999', fontStyle: 'italic' }}>
                      <strong>Question:</strong>
                      <p>Question text not available</p>
                    </div>
                  )}
                  
                  {/* Correct Answer */}
                  {quiz.correct_answer ? (
                    <div className="review-correct-answer">
                      <strong>Correct Answer:</strong>
                      <p>{quiz.correct_answer}</p>
                    </div>
                  ) : (
                    <div className="review-correct-answer" style={{ opacity: 0.6 }}>
                      <strong>Correct Answer:</strong>
                      <p>Correct answer not available</p>
                    </div>
                  )}
                  
                  {/* Metadata */}
                  <div className="review-question-meta">
                    <span className="review-meta-item">
                      <strong>Subject:</strong> {quiz.subject_name || 'N/A'}
                    </span>
                    <span className="review-meta-item">
                      <strong>Topic:</strong> {quiz.topic_name || 'N/A'}
                    </span>
                    <span className="review-meta-item">
                      <strong>Score:</strong> {quiz.scoreAwarded || 0} points
                    </span>
                  </div>
                  <div className="review-question-date">
                    {quiz.created_at ? new Date(quiz.created_at).toLocaleDateString() : 'N/A'}
                  </div>
                </div>
              )}
            </div>
            );
          })}
        </div>
      )}
      
      {/* Treats Collected and Survey buttons - Always visible */}
      <div className="review-done-button-container">
        <button className="review-treats-collected-button app-btn" onClick={handleTreatsCollected}>
          Treats Collected
        </button>
        <button className="review-survey-button app-btn" onClick={() => window.open(SURVEY_URL, '_blank', 'noopener,noreferrer')}>
          Click here to complete a survey & receive a gift!
        </button>
      </div>
      
      {/* Completion Popup */}
      {showCompletionPopup && (
        <div className="review-completion-popup-overlay" onClick={closeCompletionPopup}>
          <div className="review-completion-popup" onClick={e => e.stopPropagation()}>
            <img 
              src="/CatTreat.png" 
              alt="Congratulations!" 
              className="review-completion-image"
            />
            <p className="review-completion-message">You have earned 1 treat for Itzy</p>
            <button className="review-completion-button app-btn" onClick={closeCompletionPopup}>
              Continue
            </button>
          </div>
        </div>
      )}

      {/* Treats Collected Popup */}
      {showTreatsCollectedPopup && (
        <div className="review-treats-popup-overlay" onClick={closeTreatsCollectedPopup}>
          <div className="review-treats-popup" onClick={e => e.stopPropagation()}>
            <h2 className="review-treats-popup-title">Treats Collected</h2>
            <div className="review-treats-grid">
              {Array.from({ length: 8 }, (_, index) => {
                const treatNumber = index + 1;
                const isUnlocked = treatNumber <= completedReviewsCount;
                return (
                  <div 
                    key={treatNumber} 
                    className={`review-treat-slot ${isUnlocked ? 'unlocked' : 'locked'}`}
                  >
                    {isUnlocked ? (
                      <img 
                        src="/CatTreat.png" 
                        alt={`Treat ${treatNumber}`}
                        className="review-treat-image"
                      />
                    ) : (
                      <div className="review-treat-locked">
                        <span className="lock-icon">🔒</span>
                      </div>
                    )}
                    <span className="review-treat-number">{treatNumber}</span>
                  </div>
                );
              })}
            </div>
            <button className="review-completion-button app-btn" onClick={closeTreatsCollectedPopup}>
              Close
            </button>
          </div>
        </div>
      )}

      {/* Video Popup - Shows when 8th treat is unlocked */}
      {showVideoPopup && (
        <div className="review-video-popup-overlay" onClick={closeVideoPopup}>
          <div className="review-video-popup" onClick={e => e.stopPropagation()}>
            <video 
              ref={videoRef}
              src="/Itzy_cookie.mp4" 
              className="review-video-content"
              autoPlay
              controls
              muted
              onEnded={handleVideoEnded}
            >
              Your browser does not support the video tag.
            </video>
            <button className="review-completion-button app-btn" onClick={closeVideoPopup}>
              Continue
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ReviewQuestions;
